/**
 * 🗺️ AREAS PAGE — Discovery hub for the game's dungeons + zones.
 *
 * Mar 4, 2026 owner canon: replaces the Loot slot on the BottomTabBar.
 * Currently lists the Dream Forest solo dungeon; future zones (Chief
 * Wesley Blanks' quest chain, the Mossy Courtyard's deeper passages,
 * Ashen Bamboo Vale II+, etc.) will land here as they ship.
 *
 * Each Area card surfaces:
 *   • Icon + name + tagline
 *   • "First run: prep pass" / "Boss unlocked" status pulled from the
 *     dungeon's own /state endpoint when available
 *   • CTA button routing to the actual play surface
 *
 * The page is intentionally SPARSE — a directory, not a lore doc. Zone
 * cinematics/lore live on the target pages themselves.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AreasPage() {
  const nav = useNavigate();
  const [dfState, setDfState] = useState(null);

  useEffect(() => {
    axios.get(`${API}/dream-forest/state`)
      .then((r) => setDfState(r?.data || null))
      .catch(() => setDfState(null));
  }, []);

  return (
    <div className="min-h-screen bg-[#050B0A] text-white p-4 pb-24" data-testid="areas-page">
      <header className="max-w-2xl mx-auto mb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Compass</div>
        <h1 className="mt-1 font-display text-4xl leading-tight text-[#FCD34D]">
          Areas & Dungeons
        </h1>
        <p className="mt-2 text-sm text-neutral-400">
          Every explorable place you've heard whispers of. New areas slip in as your
          journey deepens — check back when a Chief mentions somewhere in passing.
        </p>
      </header>

      <div className="max-w-2xl mx-auto space-y-3">
        <AreaCard
          testid="area-dream-forest"
          icon="🌲"
          name="The Dream Forest"
          tagline="A purple-lit wood where the sky doesn't move and the mobs remember."
          tier="Solo · Beginner"
          status={
            dfState == null
              ? "Loading…"
              : `${dfState.runs_completed} run${dfState.runs_completed === 1 ? "" : "s"} completed`
          }
          statusHue={dfState?.boss_unlocked ? "emerald" : "amber"}
          cta="Enter Forest"
          onEnter={() => nav("/dream-forest")}
        />

        {/* 🔮 Placeholder for the next zone — surfaces as "coming soon"
            so the discovery cadence feels alive. Replace/remove once
            Chief Wesley Blanks' quest zone lands. */}
        <AreaCard
          testid="area-locked-next"
          icon="🌫️"
          name="???"
          tagline="Someone whispered a name to Chief Wesley Blanks. He hasn't shared it yet."
          tier="Solo · Locked"
          status="🔒 Locked — return after the quest lands"
          statusHue="neutral"
          cta="Locked"
          disabled
        />
      </div>
    </div>
  );
}

function AreaCard({ testid, icon, name, tagline, tier, status, statusHue, cta, onEnter, disabled }) {
  const hueTint = {
    emerald: "text-emerald-300 border-emerald-800/40 bg-emerald-950/20",
    amber:   "text-amber-200 border-amber-800/40 bg-amber-950/20",
    neutral: "text-neutral-500 border-neutral-800/40 bg-neutral-950/40",
  }[statusHue || "neutral"];
  return (
    <article
      className={`rounded-xl border p-4 ${disabled ? "border-neutral-800/60 bg-neutral-950/40 opacity-60" : "border-neutral-800 bg-neutral-950/60 hover:border-[#FCD34D]/50 transition-colors"}`}
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        <div className="text-4xl leading-none pt-0.5">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className={`font-black text-lg truncate ${disabled ? "text-neutral-400" : "text-amber-100"}`}>{name}</h2>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 shrink-0">{tier}</div>
          </div>
          <p className="text-xs text-neutral-400 mt-1 leading-relaxed">{tagline}</p>
          <div className={`mt-3 text-xs rounded-md border px-2 py-1.5 inline-block ${hueTint}`}>
            {status}
          </div>
        </div>
      </div>
      <button
        onClick={onEnter}
        disabled={disabled}
        data-testid={`${testid}-cta`}
        className={`mt-3 w-full py-2.5 uppercase tracking-widest text-xs font-black rounded ${
          disabled
            ? "bg-neutral-900 text-neutral-600 cursor-not-allowed"
            : "bg-[#FCD34D] text-black hover:bg-[#F59E0B]"
        }`}
      >
        {cta}
      </button>
    </article>
  );
}
